import React, { useMemo, useRef } from "react";

// Rotating micro-line system. One line at a time, near the input area or
// empty state. Picked randomly on every fresh app open AND on each chat
// switch (driven by the `rotateKey` prop). Soft fade, low-emphasis
// typography — no popups, no clutter.
const LINES = [
    "Tone shapes outcomes.",
    "A calmer message can change the conversation.",
    "Clarity matters. Tone does too.",
    "Sometimes the second draft is the better conversation.",
    "Not every honest message has to sound harsh.",
    "The right words matter. The right tone matters more.",
    "Difficult conversations deserve thoughtful language.",
    "A pause before sending can change everything.",
    "Being direct and being respectful can coexist.",
    "Good communication is emotional intelligence in action.",
];

const randomLine = (avoid) => {
    if (!avoid) return LINES[Math.floor(Math.random() * LINES.length)];
    // Pick something different from `avoid` when possible — keeps a new
    // chat switch from sometimes landing back on the same line.
    const pool = LINES.filter((l) => l !== avoid);
    return pool[Math.floor(Math.random() * pool.length)];
};

// Module-level memo so two MicroLine instances mounted in the same render
// tree (e.g. empty-state + composer footer) display the same rotation —
// keyed by whatever `rotateKey` they're sharing. A new key = new pick.
let lastKey = "__init__";
let lastLine = randomLine(null);

const lineForKey = (key) => {
    const normalized = key == null ? "__null__" : String(key);
    if (normalized === lastKey) return lastLine;
    lastLine = randomLine(lastLine);
    lastKey = normalized;
    return lastLine;
};

/**
 * MicroLine — single calm line near the composer / empty state.
 *
 * Props:
 *  • variant: "footer" | "empty"
 *  • rotateKey: when this value changes, the displayed line rotates to a
 *               fresh random pick (different from the previous line when
 *               possible). Pass the active chat id from the parent so
 *               switching chats picks a new line for both slots in sync.
 */
const MicroLine = ({ variant = "footer", rotateKey, className = "" }) => {
    // Stable per-mount fallback key (so omitting rotateKey still works).
    const fallbackKey = useRef(`mount-${Math.random().toString(36).slice(2)}`);
    const effectiveKey = rotateKey != null ? rotateKey : fallbackKey.current;

    const line = useMemo(() => lineForKey(effectiveKey), [effectiveKey]);

    const base =
        variant === "empty"
            ? "text-[12.5px] text-muted-foreground/80"
            : "text-[11.5px] text-muted-foreground";

    // `key` on the <p> retriggers the fade-in CSS animation each rotation.
    return (
        <p
            key={`${variant}-${effectiveKey}`}
            data-testid="micro-line"
            data-variant={variant}
            className={`tg-microline ${base} text-center italic font-[450] tracking-[0.01em] ${className}`}
        >
            {line}
        </p>
    );
};

export default MicroLine;
